/**
 * Firestore initialization.
 *
 * Three non-obvious choices:
 *
 *   1. Multi-tab persistence (`persistentMultipleTabManager`).
 *      The default tab manager (`persistentSingleTabManager`) only
 *      allows ONE tab to own the IndexedDB-backed cache. When a user
 *      has multiple `prest0.ai` tabs open, every tab after the first
 *      throws:
 *
 *        FirebaseError: [code=failed-precondition]: Failed to obtain
 *        exclusive access to the persistence layer. To allow shared
 *        access, multi-tab synchronization has to be enabled in all
 *        tabs.
 *
 *      The SDK then falls back to memory cache, which is fine
 *      functionally — but the failed-precondition error appears in
 *      DevTools and historically has been mistaken for a cause of
 *      stuck loading screens. Switching to
 *      `persistentMultipleTabManager` lets N tabs share the same
 *      IndexedDB without that failure.
 *
 *   2. THE OAUTH POPUP NEVER JOINS THE PERSISTENCE LEASE (2026-08-19,
 *      owner-reported production crash). The connect-app popup lands on
 *      /integrations/callback, which boots this whole SPA — so a SECOND
 *      multi-tab persistence client joined the shared IndexedDB lease,
 *      and the opener then closes the popup the instant its 3s poll sees
 *      the connection land, tearing one lease holder down mid-write.
 *      That is the classic trigger for
 *      `FIRESTORE INTERNAL ASSERTION FAILED: Unexpected state (ID:
 *      b815/ca9)`, which unmounted the entire app into the red error
 *      boundary. The popup is short-lived and renders a static landing —
 *      it needs no local cache, so it gets the in-memory default
 *      (plain getFirestore()) and never touches the lease. Detection
 *      matches the popup by its shared window name (set by
 *      connectPopupFlow's window.open) or the callback path itself.
 *
 *   3. Synchronous initialization is wrapped in try/catch and falls
 *      back to a plain `getFirestore()` if `initializeFirestore`
 *      throws (e.g. an unforeseen environment that can't open
 *      IndexedDB). The fallback Firestore still works against the
 *      network — it just won't have a local cache. Critically: this
 *      failure must NEVER bubble up to the React render tree, because
 *      every consumer of `db` imports it as a module-level default
 *      export and would crash app boot if this file threw.
 */
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  getFirestore,
  type Firestore,
} from 'firebase/firestore';
import app from './firebase';

// Keep in sync with CONNECT_POPUP_WINDOW_NAME in
// src/services/composio/connectPopupFlow.ts — imported literally here so
// this module keeps ZERO dependencies beyond the firebase app (it must be
// safe to import first, from index.tsx, before any code-split chunk).
const OAUTH_POPUP_WINDOW_NAME = 'prest0n-connect';

function isOAuthPopupWindow(): boolean {
  try {
    return (
      window.name === OAUTH_POPUP_WINDOW_NAME ||
      window.location.pathname === '/integrations/callback'
    );
  } catch {
    return false;
  }
}

let db: Firestore;

try {
  if (isOAuthPopupWindow()) {
    // Memory-only: never a party to the multi-tab IndexedDB lease (see #2).
    db = getFirestore(app);
  } else {
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
  }
} catch (err) {
  // initializeFirestore can throw if it has already been called for
  // this app (e.g. HMR re-import), or in environments where the SDK
  // cannot open IndexedDB. In either case we want app boot to
  // continue with a working — if uncached — Firestore instance,
  // never a top-level loading-gate hang.
  // eslint-disable-next-line no-console
  console.warn(
    '[firestore] initializeFirestore failed, falling back to default getFirestore(). ' +
      'Local persistence cache is disabled for this session.',
    err,
  );
  db = getFirestore(app);
}

export default db;
